import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, filter, finalize, map, Observable, throwError } from 'rxjs';

import { Route } from '@ripeurs/common/types/common/route';

import { environment } from 'src/environments/environment';
import { LocalStorage } from '~app/common/decorator/local-storage.decorator';

import { HttpParamsQS } from '../helpers/http';
import { DebugService } from './debug.service';
import { DialogService } from './dialog.service';
import { UiService } from './ui.service';


@Injectable({ providedIn: 'root' })
export class ApiV2Service {
	private readonly config = { url: environment.app.apiV2.url };
	@LocalStorage('user_token') private readonly token: string | null | undefined;

	constructor(
		private readonly _debug: DebugService,
		private readonly _dialog: DialogService,
		private readonly _http: HttpClient,
		private readonly _ui: UiService,
	) {}

	public request<R extends Route.IO = Route.IO>(
		{ method, path }: Route.Config,
		requestData: R['request'],
		{ reportProgress, loaderHeader } = { reportProgress: false, loaderHeader: true },
	): Observable<any> {
		const { params = {}, query = {} } = requestData;
		const body = requestData.body;
		const ripID = Math.round(Math.random() * 1000000000)
			.toString(36)
			.toUpperCase()
			.replace(/[01OIL]/gi, '8');
		const headers: { [k: string]: string } = { 'Content-Type': 'application/json', 'X-Ripeurs-Rid': ripID };
		if (this.token) headers['X-Auth-Token'] = this.token;

		let reqPath = path;
		for (const paramName in params) {
			reqPath = reqPath.replace(':' + paramName, encodeURIComponent(params[paramName]));
		}

		if (reqPath[0] === '/') reqPath = reqPath.slice(1);

		const reqQuery = new HttpParamsQS({ fromObject: query });

		let reqBody: string | FormData | null;

		if (body instanceof FormData) {
			reqBody = <FormData>body;
			delete headers['Content-Type'];
		} else if (body !== undefined && body !== null) {
			reqBody = JSON.stringify(body);
		} else {
			reqBody = null;
			delete headers['Content-Type'];
		}

		const req = new HttpRequest(
			method,
			this.config.url + reqPath,
			reqBody,
			{
				params: reqQuery,
				headers: new HttpHeaders(headers),
				responseType: 'json',
				reportProgress: reportProgress,
			},
		);
		const reqClone = req.clone();

		if (loaderHeader) { this._ui.header.set({ loaderBarShow: true }); }

		return this._http.request(req).pipe(
			filter(event => reportProgress || event instanceof HttpResponse),
			map(httpResponse => reportProgress ? httpResponse : (httpResponse as HttpResponse<R>).body),
			catchError((error: HttpErrorResponse) => this.requestError(error, reqClone)),
			finalize(() => {
				if (loaderHeader) { this._ui.header.set({ loaderBarShow: false }); }
			}),
		);
	}

	private requestError<T>(error: HttpErrorResponse, request: HttpRequest<T>): Observable<never> {
		if (error.status === 401) return throwError(() => error);

		let title = 'Erreur lors de la requete';
		let content = `
			Une erreur s'est produit entre toi et le serveur, essaye de recommencer.
			Si le problème persiste, envoie un rapport de bug automatique en cliquant sur l'icône en haut à droite.
		`;
		if (!navigator.onLine) {
			title = 'Problème de connexion';
			content = `Vérifie ta connexion internet, et recommence.`;
		}

		this._dialog.openMessage({
			title: title,
			content: content,
			debugId: `HTTP: ${error.status || 'NETWORK'} · RID-V2: ${request.headers.get('X-Ripeurs-Rid')}`,
			buttons: [{ title: `Ok` }],
			debug: () => {
				const requestDebug = {
					error: {
						status: error.status,
						statusText: error.statusText,
						message: error.message,
						response: error.error,
					},
					method: request.method,
					url: request.url,
					urlWithParams: request.urlWithParams,
					headers: (() => {
						const headers: { [k: string]: string | null } = {};
						for (const key of request.headers.keys()) {
							headers[key] = key.toLowerCase() === 'x-auth-token'
								? '[MASKED]'
								: request.headers.get(key);
						}
						return headers;
					})(),
					params: (<HttpParamsQS>request.params).__params,
					body: (() => {
						if (request.body instanceof FormData) return 'FormData';
						return JSON.parse(<string>request.body);
					})(),
				};

				this._debug.mailDebug('HTTP-Global', requestDebug);
			},
		});

		console.error('API-V2 request failed', {
			requestId: request.headers.get('X-Ripeurs-Rid'),
			status: error.status,
			url: request.urlWithParams,
		});
		return throwError(() => error);
	}
}
